import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api, { convertFileToBase64 } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, MapPin, Phone, Clock, Mail, Image as ImageIcon, Building2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const empty = { name: "", slug: "", address: "", city: "", phone: "", phone2: "", manager_email: "", hours: "10:00 - 22:00", map_url: "", facilities: [], gallery: [], hero_image: "", description: "", status: "active" };

export default function AdminBranchesCMS() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saveBusy, setSaveBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/branches");
      setList(r.data);
    } catch {
      toast.error("Failed to load branches");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name || !form.city) return toast.error("Branch name and city are required");
    setSaveBusy(true);
    try {
      if (editing) await api.put(`/admin/branches/${editing}`, form);
      else await api.post("/admin/branches", form);
      toast.success("Branch saved successfully");
      setOpen(false);
      load();
    } catch { toast.error("Failed to save branch"); }
    finally { setSaveBusy(false); }
  };

  const del = async (id) => {
    if (!confirm("Delete branch?")) return;
    await api.delete(`/admin/branches/${id}`);
    toast.success("Branch deleted");
    load();
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="premium-spinner mb-3"></div>
        <div className="text-xs text-amber-400 font-bold uppercase tracking-widest animate-pulse">Loading Branches CMS...</div>
      </div>
    );
  }

  return (
    <div data-testid="admin-branches-cms" className="space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b-2 border-white/10 pb-6"
      >
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-emerald-400 font-extrabold mb-1 flex items-center gap-2">
            <Building2 size={16} /> LOCATIONS & FACILITIES
          </div>
          <h1 className="text-4xl lg:text-5xl font-heading text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-hgold to-emerald-400 font-bold">
            Branches CMS
          </h1>
        </div>

        <button
          onClick={() => { setEditing(null); setForm(empty); setOpen(true); }}
          className="gold-btn rounded-xl py-3 px-6 font-extrabold text-xs uppercase tracking-wider shadow-lg shadow-yellow-950/40 flex items-center gap-2"
          data-testid="branch-add-btn"
        >
          <Plus size={16} /> Add Branch
        </button>
      </motion.div>

      {/* Branch Cards Grid */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
      >
        {list.map((b) => (
          <div
            key={b.id}
            className="rounded-2xl border border-amber-400/70 overflow-hidden bg-gradient-to-b from-hpaper via-black/90 to-hpaper shadow-xl shadow-yellow-950/20 hover:scale-[1.02] transition-all flex flex-col justify-between"
          >
            <div>
              <div className="aspect-video relative overflow-hidden bg-black/60">
                <img src={b.hero_image || "https://images.pexels.com/photos/3757942/pexels-photo-3757942.jpeg"} alt="" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-80" />
                <div className="absolute bottom-3 left-4 right-4">
                  <span className="text-[10px] font-extrabold uppercase tracking-widest px-3 py-1 rounded-full border border-amber-400/50 bg-amber-950/90 text-amber-300 shadow-md">
                    {b.city}
                  </span>
                </div>
              </div>

              <div className="p-5 space-y-2">
                <h3 className="font-heading text-2xl font-bold text-white">{b.name}</h3>
                <div className="space-y-1 text-xs text-white/70">
                  <div className="flex items-center gap-2">
                    <MapPin size={13} className="text-amber-400 shrink-0" />
                    <span className="truncate">{b.address || b.city}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone size={13} className="text-amber-400 shrink-0" />
                    <span>{b.phone}</span>
                  </div>
                  {b.phone2 && (
                    <div className="flex items-center gap-2 text-white/80">
                      <Phone size={13} className="text-amber-400/70 shrink-0" />
                      <span>{b.phone2}</span>
                    </div>
                  )}
                  {b.hours && (
                    <div className="flex items-center gap-2 text-emerald-400 font-semibold">
                      <Clock size={13} className="shrink-0" />
                      <span>{b.hours}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="p-5 pt-0 flex items-center gap-2">
              <button
                onClick={() => { setEditing(b.id); setForm({ ...empty, ...b }); setOpen(true); }}
                className="flex-1 py-2.5 rounded-xl border border-amber-400/60 bg-amber-950/30 hover:bg-amber-500/20 text-amber-300 text-xs uppercase font-extrabold tracking-wider transition-all flex items-center justify-center gap-1.5"
              >
                <Pencil size={13} /> Edit Branch
              </button>
              <button
                onClick={() => del(b.id)}
                className="p-2.5 rounded-xl border border-red-500/40 bg-red-950/20 hover:bg-red-900/40 text-red-300 transition-all"
              >
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}

        {list.length === 0 && (
          <div className="col-span-full text-center py-16 text-white/50 bg-hpaper border border-white/10 rounded-2xl font-medium">
            No branches added yet. Click "Add Branch" to create one.
          </div>
        )}
      </motion.div>

      {/* Dialog Modal */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0c0c0c] border border-amber-400/80 rounded-2xl text-white max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl p-6 lg:p-8">
          <DialogHeader className="border-b border-white/10 pb-4">
            <DialogTitle className="font-heading text-3xl text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-hgold to-emerald-400 font-bold">
              {editing ? "Edit Branch Location" : "Add Branch Location"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-5 mt-4">
            <div>
              <label className="text-sm font-semibold text-white/90 block mb-1.5">Branch Full Name</label>
              <input
                placeholder="e.g. Hammam Lajpat Nagar"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full bg-black/70 border border-white/20 focus:border-hgold px-4 py-3 text-sm text-white outline-none rounded-xl transition-all"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="text-sm font-semibold text-white/90 block mb-1.5">URL Slug</label>
                <input
                  placeholder="e.g. lajpat-nagar"
                  value={form.slug}
                  onChange={(e) => setForm({ ...form, slug: e.target.value })}
                  className="w-full bg-black/70 border border-white/20 focus:border-hgold px-4 py-3 text-sm text-white outline-none rounded-xl transition-all"
                />
              </div>
              <div>
                <label className="text-sm font-semibold text-white/90 block mb-1.5">City Location</label>
                <input
                  placeholder="e.g. New Delhi"
                  value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                  className="w-full bg-black/70 border border-white/20 focus:border-hgold px-4 py-3 text-sm text-white outline-none rounded-xl transition-all"
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-semibold text-white/90 block mb-1.5">Full Street Address</label>
              <input
                placeholder="e.g. E-24, Central Market, Lajpat Nagar II, New Delhi"
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                className="w-full bg-black/70 border border-white/20 focus:border-hgold px-4 py-3 text-sm text-white outline-none rounded-xl transition-all"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <div>
                <label className="text-sm font-semibold text-white/90 block mb-1.5">Phone 1</label>
                <input
                  placeholder="+91 9560656913"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full bg-black/70 border border-white/20 focus:border-hgold px-4 py-3 text-sm text-white outline-none rounded-xl transition-all"
                />
              </div>
              <div>
                <label className="text-sm font-semibold text-white/90 block mb-1.5">Phone 2 (Optional)</label>
                <input
                  placeholder="+91 7065953969"
                  value={form.phone2 || ""}
                  onChange={(e) => setForm({ ...form, phone2: e.target.value })}
                  className="w-full bg-black/70 border border-white/20 focus:border-hgold px-4 py-3 text-sm text-white outline-none rounded-xl transition-all"
                />
              </div>
              <div>
                <label className="text-sm font-semibold text-white/90 block mb-1.5">Operating Hours</label>
                <input
                  placeholder="10:00 - 22:00"
                  value={form.hours}
                  onChange={(e) => setForm({ ...form, hours: e.target.value })}
                  className="w-full bg-black/70 border border-white/20 focus:border-hgold px-4 py-3 text-sm text-white outline-none rounded-xl transition-all"
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-semibold text-white/90 block mb-1.5">Manager Email Address</label>
              <input
                placeholder="manager@hammam.com"
                value={form.manager_email}
                onChange={(e) => setForm({ ...form, manager_email: e.target.value })}
                className="w-full bg-black/70 border border-white/20 focus:border-hgold px-4 py-3 text-sm text-white outline-none rounded-xl transition-all"
              />
            </div>

            {/* Photo Upload */}
            <div>
              <label className="text-sm font-semibold text-white/90 block mb-1.5">Branch Cover Hero Image</label>
              {form.hero_image ? (
                <div className="flex items-center gap-3">
                  <div className="w-36 h-24 rounded-xl border-2 border-amber-400/80 overflow-hidden bg-black flex items-center justify-center shadow-md">
                    <img src={form.hero_image} alt="Preview" className="w-full h-full object-cover" />
                  </div>
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, hero_image: "" })}
                    className="text-xs border border-red-500/40 bg-red-950/20 text-red-300 hover:bg-red-900/40 px-3 py-2 rounded-xl font-bold uppercase tracking-wider transition-all"
                  >
                    Remove Photo
                  </button>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center w-full h-28 rounded-2xl border-2 border-dashed border-white/20 hover:border-hgold cursor-pointer bg-black/40 hover:bg-black/60 transition-all p-4 text-center">
                  <ImageIcon className="w-6 h-6 text-amber-400 mb-1" />
                  <span className="text-xs font-bold text-white">Click to upload branch cover photo</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        try {
                          const base64 = await convertFileToBase64(file);
                          setForm({ ...form, hero_image: base64 });
                        } catch {
                          toast.error("Failed to read file");
                        }
                      }
                    }}
                    className="hidden"
                  />
                </label>
              )}
            </div>

            <div>
              <label className="text-sm font-semibold text-white/90 block mb-1.5">Google Maps Embed URL</label>
              <input
                placeholder="https://maps.google.com/..."
                value={form.map_url}
                onChange={(e) => setForm({ ...form, map_url: e.target.value })}
                className="w-full bg-black/70 border border-white/20 focus:border-hgold px-4 py-3 text-sm text-white outline-none rounded-xl transition-all"
              />
            </div>

            <div>
              <label className="text-sm font-semibold text-white/90 block mb-1.5">Short Location Description</label>
              <textarea
                rows={3}
                placeholder="Description of the branch, location landmarks..."
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="w-full bg-black/70 border border-white/20 focus:border-hgold px-4 py-3 outline-none rounded-xl resize-none text-sm text-white transition-all"
              />
            </div>

            <button
              onClick={save}
              disabled={saveBusy}
              className="gold-btn w-full justify-center py-4 rounded-xl font-extrabold text-xs uppercase tracking-wider shadow-lg shadow-yellow-950/40 flex items-center gap-2 mt-4"
            >
              {saveBusy ? "Saving Branch..." : "Save Branch Location"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
